import { ChangeEvent, useEffect, useState, useCallback } from 'react';

import { SelectedProps } from '@components/elements/DropdownModal';
import {
  BSC_SCAN_URL,
  CONTRACT_ADDRESS,
  STAKE_BNB_FEE,
  TIFI_ADDRESS,
  TOKENS,
} from '@config/constants';
import Notice from "../components/Notice";
import { MouseEvent } from "react";
import SwapCard from '@features/Swap/components/SwapCard';
import Button from '@components/elements/Button';
import { useWeb3React } from '@web3-react/core';
import ConnectWallet from '@components/widget/connect-wallet';
import { ethers } from 'ethers';
import { minABI } from '@config/TiFi_min_abi';
import { T } from 'react-translator-component';
import { extendToBigNumber, getErrorMessage } from '@utils/tifi';
import StakeABI from '@config/abi/TiFiReservior.json';
import StakeWidget from '../components/StakeWidget';
import { useNavigate } from 'react-router-dom';
import { useAlertActions } from '@hooks/useAlert';
import tifiRoutes from '@components/router/tifiRoutes';

const StakeAdd = () => {
  const navigate = useNavigate();
  const [selectedCurrency, setSelectedCurrency] = useState<SelectedProps>(
    TOKENS[3],
  );
  const [openConnectModal, setOpenConnectModal] = useState<boolean>(false);
  const { account: address, library: provider } = useWeb3React();
  const [amount, setAmount] = useState<any>('');
  const [balance, setBalance] = useState<number>(0);
  const [allowAmount, setAllowAmount] = useState<number>(0);
  const [availableBalance, setAvailableBalance] = useState(true);
  const [status, setStatus] = useState(false);
  const { setAlert } = useAlertActions();
  const [showNotice, setShowNotice] = useState(true);

  // HandleChange
  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const rgx = /^[0-9]*(\.\d{0,9})?$/;
    let result = e.target.value.toString().match(rgx);
    if (!result) {
      return;
    }
    let tmpval: any = e.target.value ? e.target.value : 0;

    if (tmpval < 0 || isNaN(tmpval)) {
      tmpval = amount;
    } else if (
      !(
        typeof tmpval === 'string' &&
        (tmpval.endsWith('.') || tmpval.startsWith('.') || tmpval.endsWith('0'))
      )
    ) {
      tmpval = Number(e.target.value.toString());
    }
    setAvailableBalance(tmpval <= balance);
    setAmount(tmpval);
  };
  // Get Balance
  const getBalance = useCallback(async () => {
    const signer = provider.getSigner();
    let contract = new ethers.Contract(TIFI_ADDRESS, minABI, signer);
    try {
      if (address != null) {
        const tifiBal = await contract.balanceOf(address);
        setBalance(Math.floor(Number(tifiBal._hex) / Number(10 ** 18))); // TiFi Decimal is 18
      } else {
        return 0;
      }
    } catch (error) {
      return 0;
    }
  }, [address, provider]);

  const handleMax = async () => {
    setAvailableBalance(true);
    setAmount(balance);
  };

  // Check Allowance
  const checkAllowance = useCallback(async () => {
    const signer = provider.getSigner();
    const contract = new ethers.Contract(TIFI_ADDRESS, minABI, signer);
    const amount = await contract.allowance(
      address,
      CONTRACT_ADDRESS.RESERVIOR_ADDRESS,
    );
    setAllowAmount(amount / 10 ** 18);
  }, [address, provider]);

  // Handle Approve
  const handleApprove = async () => {
    setStatus(true);
    try {
      const contract = new ethers.Contract(
        TIFI_ADDRESS,
        minABI,
        provider.getSigner(),
      );
      const tx = await contract.approve(
        CONTRACT_ADDRESS.RESERVIOR_ADDRESS,
        '1000000000000000000000000000000000000',
      );
      await tx.wait();

      setAlert({
        message: T('Approved! Now you can stake.'),
        type: 'success',
        url: {
          link: `${BSC_SCAN_URL}/tx/${tx.hash}`,
          text: 'Check Transaction on BSCScan',
        },
      });
      await checkAllowance();
    } catch (error: any) {
      console.log(error);
      const supplyError = getErrorMessage(error);
      setAlert({
        message: supplyError,
        type: 'error',
        url: {
          link: '',
          text: '',
        },
      });
    }
    setStatus(false);
  };

  // Handle Stake
  const handleStake = async () => {
    setStatus(true);
    if (address != null) {
      try {
        const stakeContract = new ethers.Contract(
          CONTRACT_ADDRESS.RESERVIOR_ADDRESS,
          StakeABI.abi,
          provider.getSigner(),
        );

        const tx = await stakeContract.deposit(extendToBigNumber(amount), {
          value: ethers.utils.parseUnits(STAKE_BNB_FEE, 'ether')._hex,
        });

        setAlert({
          message: T('Transaction submitted! Please wait a few seconds for completion.'),
          type: 'notice',
          url: {
            link: `${BSC_SCAN_URL}/tx/${tx.hash}`,
            text: 'Check Transaction on BSCScan',
          },
        });

        await tx.wait();

        setAlert({
          message: 'Stake Success!',
          type: 'success',
          url: {
            link: `${BSC_SCAN_URL}/tx/${tx.hash}`,
            text: 'Check Transaction on BSCScan',
          },
        });
        setAmount('0');
        await getBalance();
        navigate(tifiRoutes.STAKE);
      } catch (error: any) {
        setAlert({
          message: T(getErrorMessage(error as any)),
          type: 'error',
          url: {
            link: '', text: '',
          }
        });
      }
      setStatus(false);
    }
  };

  const handleAddToken = async (token: SelectedProps) => {
    if (!provider) {
      setAlert({
        message: 'Please connect to wallet first!',
        type: 'notice',
        url: {
          link: ``,
          text: '',
        },
      });
      return
    }
    try {
      let decimal;
      let contract;
      const signer = provider.getSigner();
      contract = new ethers.Contract(token.address, minABI, signer);
      decimal = await contract.decimals();
      const wasAdded = await window.ethereum.request({
        method: "wallet_watchAsset",
        params: {
          type: "ERC20", // Initially only supports ERC20, but eventually more!
          options: {
            address: token.address,
            symbol: token.title, // A ticker symbol or shorthand, up to 5 chars.
            decimals: decimal,
          },
        },
      });
      if (wasAdded) {
        setAlert({
          message: `${token.title} ${T("successful added")}`,
          type: 'success',
          url: {
            link: '',
            text: '',
          },
        });
      } else {
        setAlert({
          message: `${token.title} ${T("added failed")}`,
          type: 'error',
          url: {
            link: '',
            text: '',
          },
        });
      }
    } catch (error) {
      setAlert({
        message: `${T("Cannot add address of")} TIFI Token, ${T(getErrorMessage(error))}`,
        type: 'error',
        url: {
          link: '',
          text: '',
        },
      });
    }
  };

  useEffect(() => {
    if (address && provider) {
      setAmount('0');
      getBalance();
      checkAllowance();
    }
  }, [address, provider, getBalance, checkAllowance]);
  return (

    <StakeWidget title={T('Stake TIFI')} onClick={() => navigate(-1)}>
      {
        showNotice && (
          <div className="my-4">
            <Notice text={T("Notice: TiFi Bank charges 0.02 BNB of transaction fee for every stake, there are no fees when unstake. To receive the rewards, you have to stake TIFI at least 30 days. You will also get WBNB reward if you stake for more than 180 days.")} handleClose={function (event: MouseEvent<HTMLButtonElement, globalThis.MouseEvent>): void {
              setShowNotice(false);
            }} />
          </div>
        )
      }
      <div>
        {openConnectModal && (
          <ConnectWallet modalClose={() => setOpenConnectModal(false)} />
        )}
        <div className="mb-4">
          <SwapCard
            inputValue={isNaN(amount) ? 0 : amount}
            selectedCurrency={selectedCurrency}
            setSelectedCurrency={setSelectedCurrency}
            fromText="Input"
            balance={balance}
            handleInputChange={(e) => handleChange(e)}
            amount={0}
            disabled={true}
            handleAddToken={handleAddToken}
            handleMax={handleMax}
          />
        </div>
        {address ? (
          <div>
            {allowAmount < Number(amount) && availableBalance && (
              <Button
                disabled={status}
                className="bg-primary text-white"
                label={T('Enable TIFI')}
                onClick={() => handleApprove()}
              />
            )}
            <div className="my-4">
              <Button
                disabled={
                  !availableBalance ||
                  status ||
                  allowAmount < Number(amount) ||
                  Number(amount) === 0
                }
                className="bg-primary text-white"
                label={
                  !availableBalance
                    ? T('Insufficient balance')
                    : status && allowAmount >= Number(amount)
                      ? 'Loading'
                      : T('Stake')
                }
                onClick={() => handleStake()}
                loading={status}
              />
            </div>
          </div>
        ) : (
          <Button
            className="bg-primary text-white"
            label={'Connect Wallet'}
            onClick={() => setOpenConnectModal(true)}
          />
        )}
      </div>
    </StakeWidget>
  );
};

export default StakeAdd;
